import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { Observable, combineLatest, debounceTime, map, switchMap } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipGeneratorInputBuilder } from '../../bet-generation/services/betslip-generator-input-builder.service';
import { BetslipGeneratorService } from '../../bet-generation/services/betslip-generator.service';
import { IBetslipSummary } from '../../summary/models';
import BetslipSummaryBuilder from '../../summary/services/betslip-summary-builder.service';
import { UpsellSummaryService } from './upsell-summary.service';

@Injectable()
export class UpsellWinningsService {
    constructor(
        private betslipGenerator: BetslipGeneratorService,
        private betslipGeneratorInputBuilder: BetslipGeneratorInputBuilder,
        private betslipSummaryBuilder: BetslipSummaryBuilder,
        private store: Store<IBetslipRootState>,
        private upsellSummaryService: UpsellSummaryService,
    ) {}

    getUpsellWinnings$(upsellPick: BetslipPick): Observable<{
        upsellPossibleWinnings: Decimal | null;
        possibleWinnings: Decimal | null;
        hasTaxation: boolean;
        hasAccaboost?: boolean;
    }> {
        return this.store.select(betslipSelector).pipe(
            debounceTime(50),
            switchMap((betslipState) => {
                const calculatorInput = this.betslipGeneratorInputBuilder.getCalculatorInput(betslipState);
                const currentBetslip = this.betslipGenerator.calculate(calculatorInput);

                const upsellBetslip = this.upsellSummaryService.generateSlip(betslipState, upsellPick);

                return combineLatest([
                    this.betslipSummaryBuilder.createBetslipSummary(currentBetslip).pipe(map(this.processSummary)),
                    this.betslipSummaryBuilder.createBetslipSummary(upsellBetslip).pipe(map(this.processSummary)),
                ]).pipe(
                    map(([current, upsell]) => {
                        return {
                            upsellPossibleWinnings: upsell.possibleWinnings,
                            possibleWinnings: current.possibleWinnings,
                            hasTaxation: current.hasTaxation,
                            hasAccaboost: upsell.hasAccaboost,
                        };
                    }),
                );
            }),
        );
    }

    processSummary(summary: IBetslipSummary) {
        const accaboost = summary.slipSummaries?.find((slipSummary) => !!slipSummary.rewardTokenModifications)?.rewardTokenModifications?.accaBoost;
        if (accaboost) {
            return { hasAccaboost: true, possibleWinnings: accaboost.boostedWinnings, hasTaxation: !!summary.taxation };
        }

        return { possibleWinnings: summary.possibleWinningsNet, hasTaxation: !!summary.taxation };
    }
}
